import type {
  ContourViewSettings,
  PerValueAxes,
  Points3DViewSettings,
  Surface3DViewSettings,
} from '@ardoq/xyz-charts';
import type { I18nLocale } from '@ardoq/i18n';

/**
 * What the view publishes for the settings bar to read.
 *
 * A narrow slice of the view's props rather than all of them: the settings bar only needs the
 * settings to tick the active options and the field list to populate the pickers, and the
 * points array is large enough that putting it through a stream on every change would be
 * wasteful for no reader.
 */
export type Surface3DViewProperties = {
  viewSettings: Surface3DViewSettings;
  axisFields: { name: string; label: string }[];
  i18nLocale: I18nLocale;
  /** Whether height plots a list field, whose positions cannot be summed. */
  isListHeight: boolean;
  isEmptyView: boolean;
  /**
   * Whether the chart currently has a surface, as the chart sees it.
   *
   * Not derivable from `isEmptyView`, which is the source-data check: the gridding has a failure the
   * view cannot see, and in that state the chart shows its own empty state while the source data
   * still looks fine. Published so the export menu can be disabled rather than offering a PNG that
   * comes back as a failure toast.
   */
  isDrawable: boolean;
  /** Which axes get one bin per value, which decides what the column size control offers. */
  perValueAxes: PerValueAxes;
  viewInstanceId: string;
};

const SURFACE_3D_VIEW_PROPERTIES_KEYS = [
  'viewSettings',
  'axisFields',
  'i18nLocale',
  'isEmptyView',
  'isDrawable',
  'perValueAxes',
  'viewInstanceId',
] as const satisfies readonly (keyof Surface3DViewProperties)[];

/**
 * The view-pane header reads these off an untyped record, so a mismatch would throw inside a
 * reducer rather than degrade to undefined.
 */
export const isSurface3DViewProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is Surface3DViewProperties =>
  SURFACE_3D_VIEW_PROPERTIES_KEYS.every(
    key => viewProperties[key] !== undefined
  );

/**
 * What the contour view publishes for the settings bar.
 *
 * Same shape as the surface's, since the settings bar needs the same two things from every one of
 * these charts - the settings to tick the active options, and the field list to populate the
 * pickers - and only the settings type differs.
 */
export type ContourViewProperties = {
  viewSettings: ContourViewSettings;
  axisFields: { name: string; label: string }[];
  i18nLocale: I18nLocale;
  /** Whether height plots a list field, whose positions cannot be summed. */
  isListHeight: boolean;
  isEmptyView: boolean;
  /**
   * Whether the chart currently has a contour, as the chart sees it.
   *
   * Carried for the same reason the surface carries it: the gridding has a failure the view cannot
   * see - a triangulation with area that lays no cell centre inside any of its triangles - and in
   * that state the chart shows its own empty state and mounts no canvas while the source data
   * still looks fine. Published so the export menu can be disabled rather than offering a PNG that
   * comes back as a failure toast.
   */
  isDrawable: boolean;
  viewInstanceId: string;
};

const CONTOUR_VIEW_PROPERTIES_KEYS = [
  'viewSettings',
  'axisFields',
  'i18nLocale',
  'isEmptyView',
  'isDrawable',
  'viewInstanceId',
] as const satisfies readonly (keyof ContourViewProperties)[];

export const isContourViewProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is ContourViewProperties =>
  CONTOUR_VIEW_PROPERTIES_KEYS.every(key => viewProperties[key] !== undefined);

/**
 * What the scatter and bubble views publish for the settings bar.
 *
 * Carries `showSizeVariation` because the settings bar needs it to decide whether to offer a size
 * picker, and it is not in the settings - it is which of the two views this is.
 */
export type Points3DViewProperties = {
  viewSettings: Points3DViewSettings;
  numericFields: { name: string; label: string }[];
  i18nLocale: I18nLocale;
  isEmptyView: boolean;
  viewInstanceId: string;
  showSizeVariation: boolean;
};

const POINTS_3D_VIEW_PROPERTIES_KEYS = [
  'viewSettings',
  'numericFields',
  'i18nLocale',
  'isEmptyView',
  'viewInstanceId',
  'showSizeVariation',
] as const satisfies readonly (keyof Points3DViewProperties)[];

export const isPoints3DViewProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is Points3DViewProperties =>
  POINTS_3D_VIEW_PROPERTIES_KEYS.every(
    key => viewProperties[key] !== undefined
  );
